
import { useState, useRef, useEffect } from 'react';

export default function ShareButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = 'TeamPro Management - Sports Team Equipment Platform';
  const shareText = 'Check out TeamPro - the ultimate sports team equipment management platform with amazing Valentine\'s Day deals up to 35% off!';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      setIsOpen(true);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy');
    }
  };

  const socialLinks = [
    {
      name: 'Facebook',
      icon: 'ri-facebook-fill',
      color: 'bg-[#1877F2] hover:bg-[#166FE5]',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: 'Twitter',
      icon: 'ri-twitter-x-fill',
      color: 'bg-black hover:bg-gray-800',
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    },
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-fill',
      color: 'bg-[#0A66C2] hover:bg-[#095196]',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: 'WhatsApp',
      icon: 'ri-whatsapp-fill',
      color: 'bg-[#25D366] hover:bg-[#20BD5A]',
      url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    },
    {
      name: 'Telegram',
      icon: 'ri-telegram-fill',
      color: 'bg-[#0088CC] hover:bg-[#007AB8]',
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    },
    {
      name: 'Pinterest',
      icon: 'ri-pinterest-fill',
      color: 'bg-[#E60023] hover:bg-[#D50020]',
      url: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareText)}`,
    },
    {
      name: 'Email',
      icon: 'ri-mail-fill',
      color: 'bg-gray-600 hover:bg-gray-700',
      url: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`,
    },
  ];

  return (
    <div ref={menuRef} className="fixed bottom-24 right-6 z-50">
      {/* Share Menu */}
      <div
        className={`absolute bottom-16 right-0 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300 ${
          isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        style={{ width: '280px' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-5 py-4">
          <h3 className="text-white font-bold text-lg">Share This Page</h3>
          <p className="text-teal-100 text-sm">Spread the word!</p>
        </div>

        {/* Social Links Grid */}
        <div className="p-4 grid grid-cols-4 gap-3">
          {socialLinks.map((social) => (
            <a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`${social.color} w-12 h-12 rounded-xl flex items-center justify-center transition-all hover:scale-110 cursor-pointer`}
              title={`Share on ${social.name}`}
            >
              <i className={`${social.icon} text-white text-xl`}></i>
            </a>
          ))}
        </div>

        {/* Copy Link Section */}
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-2">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="flex-1 bg-transparent text-sm text-gray-600 px-2 outline-none truncate"
            />
            <button
              onClick={copyToClipboard}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-teal-500 text-white hover:bg-teal-600'
              }`}
            >
              {copied ? (
                <span className="flex items-center gap-1">
                  <i className="ri-check-line"></i> Copied!
                </span>
              ) : (
                'Copy'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Share Button */}
      <button
        onClick={handleNativeShare}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 cursor-pointer ${
          isOpen
            ? 'bg-gray-800 rotate-45'
            : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:shadow-teal-500/50'
        }`}
        title="Share this page"
      >
        <i className={`${isOpen ? 'ri-close-line' : 'ri-share-fill'} text-white text-2xl`}></i>
      </button>

      {/* Tooltip */}
      {!isOpen && (
        <div className="absolute bottom-0 right-16 bg-gray-900 text-white text-sm px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 hover:opacity-100 pointer-events-none transition-opacity">
          Share
        </div>
      )}
    </div>
  );
}
